import { useEffect, useState } from "react";
import { Assets } from "../constants/assets";
import { WheelRewardKind } from "../types/wheel-of-luck";
import Network, { FTPURL } from "../utils/Network";
import { useAppTranslation } from "../i18n/useAppTranslation";

type CatalogEntry = { id: number; name: string; icon?: string; image?: string; photo?: string; photoFile?: string };
export type RewardPresentation = { name: string; icon?: string };
// Names are translation keys in the wheel namespace.
const resources: Record<number, RewardPresentation> = {
  1: { name: "wheel:rewards.stone", icon: "/assets/images/icons/stone.png" },
  2: { name: "wheel:rewards.iron", icon: "/assets/images/icons/metal.png" },
  3: { name: "wheel:rewards.cleg", icon: "/assets/images/icons/cleg.png" },
  8: { name: "wheel:rewards.energy", icon: Assets.icons.energy },
  9: { name: "wheel:rewards.bloodGem", icon: Assets.icons.bloodStone },
  10: { name: "wheel:rewards.ember", icon: Assets.icons.ember1x1 },
  11: { name: "wheel:rewards.dailyQuestXp", icon: "/assets/images/icons/xp.svg" },
};
// "Item #12" for a reward the server did not name.
const catalogNames: Record<number, string> = { 4: "wheel:rewards.landNumber", 5: "wheel:rewards.troopNumber", 6: "wheel:rewards.itemNumber" };

// Resources have no level; the server may still send one, so never display it.
export const rewardLevel = (reward: WheelRewardKind): number =>
  resources[reward.type] ? 0 : reward.level;

export default function useWheelRewardCatalog(enabled: boolean) {
  const { t } = useAppTranslation(["wheel"]);
  const [catalogs, setCatalogs] = useState<Record<number, CatalogEntry[]>>({});
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // The wheel response names each reward and links its image. These catalogs
    // only cover a server that does not send them yet.
    const endpoints = [[4, "/lands"], [5, "/troop"], [6, "/item"]] as const;
    endpoints.forEach(async ([type, url]) => {
      const response = await new Network().CallApi<CatalogEntry[]>({ url, method: "get" });
      if (!cancelled && response.ok && Array.isArray(response.data)) {
        setCatalogs(previous => ({ ...previous, [type]: response.data }));
      }
    });
    return () => { cancelled = true; };
  }, [enabled]);

  return (reward: WheelRewardKind): RewardPresentation => {
    const resource = resources[reward.type];
    if (resource) return { ...resource, name: t(resource.name) };
    const entry = catalogs[reward.type]?.find(item => item.id === reward.typeId);
    // Item art is per level (1-8), built the same way as the marketplace and
    // order book. A troop's icon is its still png; its image is an animated webp.
    const image = reward.image || (reward.type === 6
      ? `${FTPURL}/assets/items/item${reward.typeId}-level${Math.max(1, reward.level)}.png`
      : entry?.icon || entry?.image || entry?.photoFile || entry?.photo);
    return {
      name: reward.name || entry?.name || t(catalogNames[reward.type] || "wheel:rewards.rewardNumber", { id: reward.typeId }),
      icon: image ? (/^(https?:|\/)/.test(image) ? image : `${FTPURL}/${image}`) : undefined,
    };
  };
}
